import React from 'react';
import { Button, Box, Collapse, IconButton } from '@mui/material';
import { Close, Warning } from '@mui/icons-material';
import { useNotifications } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { Notification } from '../../types/notification';

const StickyBanner: React.FC = () => {
  const {
    getHighPriorityNotifications,
    markAsRead,
    dismissNotification,
    acknowledgeNotification
  } = useNotifications();
  const navigate = useNavigate();
  const [busy, setBusy] = React.useState(false);

  // Don't render if not authenticated
  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }

  const highPriorityNotifications = getHighPriorityNotifications();

  // Filter out dismissed and already-acknowledged notifications
  const visibleNotifications = highPriorityNotifications.filter(
    n => !n.dismissed && !(n.requiresAcknowledgement && n.acknowledgedAt)
  );

  // Show the most critical notification
  const notification = visibleNotifications[0];

  const handleDismiss = async (notificationId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await dismissNotification(notificationId);
    } catch (err) {
      console.error('Error dismissing notification:', err);
    } finally {
      setBusy(false);
    }
  };

  const handleAcknowledge = async (notificationId: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await acknowledgeNotification(notificationId);
    } catch (err) {
      console.error('Error acknowledging notification:', err);
    } finally {
      setBusy(false);
    }
  };

  const handleAction = async (notification: Notification) => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    // Best-effort read marker - never block navigation on it
    if (!notification.read) {
      markAsRead(notification._id).catch(err =>
        console.error('Error marking notification read:', err));
    }
  };

  if (!notification) {
    return null;
  }

  const getIcon = () => {
    if (notification.icon) {
      return <span style={{ fontSize: 24, marginRight: 8 }}>{notification.icon}</span>;
    }
    return <Warning />;
  };

  const needsAcknowledgement =
    !!notification.requiresAcknowledgement && !notification.acknowledgedAt;

  return (
    <Collapse in={true}>
      <Box
        sx={{
          mb: 2,
          // Soft-alert style: tinted bg with severity-dark text, readable on
          // both light pages and the dark theme (white text on tint was not)
          background: notification.priority === 'critical'
            ? 'rgba(254, 226, 226, 0.97)'
            : 'rgba(254, 243, 199, 0.97)',
          color: notification.priority === 'critical' ? '#7f1d1d' : '#78350f',
          borderRadius: { xs: 0, sm: 2 },
          p: { xs: 1.5, sm: 2 },
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          border: `1px solid ${notification.priority === 'critical'
            ? 'rgba(239, 68, 68, 0.5)' : 'rgba(245, 158, 11, 0.5)'}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: { xs: 1.5, sm: 2 }
        }}
      >
        {/* Icon */}
        <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center',
          color: notification.priority === 'critical' ? '#dc2626' : '#d97706' }}>
          {getIcon()}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.1rem' }, mb: 0.5 }}>
            {notification.title}
          </Box>
          <Box sx={{ opacity: 0.95, fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
            {notification.message}
          </Box>
          {visibleNotifications.length > 1 && (
            <Box sx={{ mt: 1, fontSize: '0.875rem', opacity: 0.9 }}>
              + {visibleNotifications.length - 1} more notification{visibleNotifications.length - 1 !== 1 ? 's' : ''} requiring attention
            </Box>
          )}
        </Box>

        {/* Actions */}
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'center', 
          flexShrink: 0,
          justifyContent: { xs: 'flex-end', sm: 'flex-start' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          {needsAcknowledgement && (
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={() => handleAcknowledge(notification._id)}
              sx={{
                backgroundColor: notification.priority === 'critical' ? '#dc2626' : '#d97706',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                '&:hover': {
                  backgroundColor: notification.priority === 'critical' ? '#b91c1c' : '#b45309'
                }
              }}
            >
              Acknowledge
            </Button>
          )}
          {notification.actionUrl && notification.actionLabel && (
            <Button
              size="small"
              variant="contained"
              onClick={() => handleAction(notification)}
              sx={{
                backgroundColor: notification.priority === 'critical' ? '#dc2626' : '#d97706',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                px: { xs: 1.5, sm: 2 },
                '&:hover': {
                  backgroundColor: notification.priority === 'critical' ? '#b91c1c' : '#b45309'
                }
              }}
            >
              {notification.actionLabel}
            </Button>
          )}
          {!notification.preventDismissal && !needsAcknowledgement && (
            <IconButton
              aria-label="close"
              size="small"
              disabled={busy}
              onClick={() => handleDismiss(notification._id)}
              sx={{
                color: notification.priority === 'critical' ? '#991b1b' : '#92400e',
                '&:hover': {
                  backgroundColor: notification.priority === 'critical'
                    ? 'rgba(220, 38, 38, 0.12)' : 'rgba(217, 119, 6, 0.12)'
                }
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
    </Collapse>
  );
};

export default StickyBanner;
